import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 just means no active session — not an error
      if (err?.response?.status !== 401) {
        console.warn("Auth check failed:", err?.message);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const signup = useCallback(async (name, email, password, ref) => {
    const url = ref ? `/auth/signup?ref=${encodeURIComponent(ref)}` : "/auth/signup";
    let acquisition = null;
    try { acquisition = JSON.parse(window.localStorage.getItem("zuntria_acq") || "null"); } catch { /* ignore */ }
    const { data } = await api.post(url, { name, email, password, acquisition });
    setUser(data.user);
    // Re-fetch full profile (onboarded flag, currency, etc.) — signup response is minimal
    try { const me = await api.get("/auth/me"); setUser(me.data); } catch (err) { console.warn("post-signup refresh failed", err); }
    return data.user;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    try { const me = await api.get("/auth/me"); setUser(me.data); } catch (err) { console.warn("post-login refresh failed", err); }
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed (continuing anyway):", err?.message);
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, signup, login, logout, refresh: checkAuth, setUser }),
    [user, loading, signup, login, logout, checkAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
